/* eslint-disable react/no-danger */
import React, { useEffect, useMemo } from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import Mention from '@tiptap/extension-mention';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import {
  getAgentSparkleIconDataUri,
  getPrimaryAccentBackground,
  getPrimaryAccentContrastText,
} from '@modernloop/shared/theme';

import { buildActivityLogLegacyTextHtml, sanitizeActivityLogCommentText } from './activityLogLegacyText';
import { parseActivityLogMarkdownBlocks, renderActivityLogMarkdownToHtml } from './activityLogMentions';
import { useAiAgentMentionLabel } from './useAiAgentLabel';

type Props = {
  legacyText?: string | null;
  markdown?: string | null;
};

const ActivityLogCommentBody = ({ legacyText, markdown }: Props) => {
  const theme = useTheme();
  const aiAgentLabel = useAiAgentMentionLabel();
  const normalizedMarkdown = useMemo(() => (markdown ? sanitizeActivityLogCommentText(markdown) : null), [markdown]);
  const renderedHtml = useMemo(
    () => (normalizedMarkdown ? renderActivityLogMarkdownToHtml(normalizedMarkdown, aiAgentLabel) : null),
    [aiAgentLabel, normalizedMarkdown]
  );
  const markdownBlocks = useMemo(
    () => (normalizedMarkdown ? parseActivityLogMarkdownBlocks(normalizedMarkdown, aiAgentLabel) : []),
    [aiAgentLabel, normalizedMarkdown]
  );
  const containsTable = markdownBlocks.some((block) => block.type === 'table');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'activity-log-mention',
        },
      }),
    ],
    content: renderedHtml ?? '',
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'activity-log-comment-body__editor',
      },
    },
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (!editor || renderedHtml === null) {
      return;
    }
    editor.commands.setContent(renderedHtml, { emitUpdate: false });
  }, [editor, renderedHtml]);

  const fallbackLabel = useMemo(() => {
    const { hasLink, text } = buildActivityLogLegacyTextHtml(legacyText ?? '');

    return (
      <Typography variant="body2" sx={{ whiteSpace: hasLink ? 'pre-wrap' : 'pre-line', wordBreak: 'break-word' }}>
        <span dangerouslySetInnerHTML={{ __html: text }} />
      </Typography>
    );
  }, [legacyText]);

  if (renderedHtml && editor) {
    const sharedContentSx = {
      '& .activity-log-comment-body__editor, & .activity-log-comment-body__content': {
        outline: 'none',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
        fontSize: (theme) => theme.typography.body1.fontSize,
        lineHeight: (theme) => theme.typography.body1.lineHeight,
        fontWeight: (theme) => theme.typography.body1.fontWeight,
        color: 'text.primary',
      },
      '& .activity-log-comment-body__editor *, & .activity-log-comment-body__content *': {
        fontFamily: 'inherit',
      },
      '& .activity-log-comment-body__editor p, & .activity-log-comment-body__content p': {
        margin: 0,
        whiteSpace: 'pre-wrap',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        color: 'inherit',
      },
      '& .activity-log-comment-body__editor p + p, & .activity-log-comment-body__content p + p': {
        mt: 1,
      },
      '& .activity-log-comment-body__editor ul, & .activity-log-comment-body__editor ol, & .activity-log-comment-body__content ul, & .activity-log-comment-body__content ol':
        {
          my: 0.5,
          pl: 3,
          fontSize: 'inherit',
          lineHeight: 'inherit',
          color: 'inherit',
        },
      '& .activity-log-comment-body__editor li > p, & .activity-log-comment-body__content li > p': {
        display: 'inline',
      },
      '& .activity-log-comment-body__editor strong, & .activity-log-comment-body__editor em, & .activity-log-comment-body__content strong, & .activity-log-comment-body__content em':
        {
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
      '& .activity-log-comment-body__editor code, & .activity-log-comment-body__content code': {
        fontFamily: 'Menlo, Consolas, Monaco, monospace',
        bgcolor: 'grey.100',
        borderRadius: 1,
        px: 0.5,
        py: 0.125,
      },
      '& .activity-log-comment-body__editor pre, & .activity-log-comment-body__content pre': {
        fontFamily: 'Menlo, Consolas, Monaco, monospace',
        bgcolor: 'grey.100',
        borderRadius: 1,
        p: 1,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
      },
      '& .activity-log-comment-body__editor a, & .activity-log-comment-body__content a': {
        color: 'primary.main',
      },
      '& .activity-log-comment-body__editor .activity-log-mention, & .activity-log-comment-body__content .activity-log-mention':
        {
          display: 'inline-block',
          borderRadius: '4px',
          px: '4px',
          py: '1px',
          fontWeight: 600,
          fontSize: 'inherit',
          lineHeight: 'inherit',
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
        },
      '& .activity-log-comment-body__editor .activity-log-mention--employee, & .activity-log-comment-body__content .activity-log-mention--employee':
        {
          background: (theme: Theme) => theme.palette.primary.tint,
          color: 'primary.dark',
        },
      '& .activity-log-comment-body__editor .activity-log-mention--agent, & .activity-log-comment-body__content .activity-log-mention--agent':
        {
          background: (theme: Theme) => getPrimaryAccentBackground(theme),
          color: (theme: Theme) => getPrimaryAccentContrastText(theme),
          pl: '22px',
          position: 'relative',
        },
      '& .activity-log-comment-body__editor .activity-log-mention--agent::before, & .activity-log-comment-body__content .activity-log-mention--agent::before':
        {
          content: '""',
          position: 'absolute',
          left: '5px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '12px',
          height: '12px',
          backgroundImage: `url("${getAgentSparkleIconDataUri(theme.palette.primary.contrastText)}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '12px 12px',
        },
      '& .activity-log-comment-body__table': {
        my: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      },
      '& .activity-log-comment-body__table thead': {
        backgroundColor: 'grey.50',
      },
      '& .activity-log-comment-body__table th, & .activity-log-comment-body__table td': {
        verticalAlign: 'top',
      },
      '& .activity-log-comment-body__table tbody tr:last-of-type td': {
        borderBottom: 'none',
      },
      '& .activity-log-comment-body__table th': {
        fontWeight: 600,
      },
    } as const;

    if (containsTable) {
      return (
        <Box sx={sharedContentSx}>
          {markdownBlocks.map((block) => {
            const blockKey =
              block.type === 'table'
                ? `table:${block.headers.join('|')}::${block.rows.map((row) => row.join('|')).join('||')}`
                : `${block.type}:${block.html}`;

            if (block.type === 'table') {
              return (
                <TableContainer key={blockKey} className="activity-log-comment-body__table">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {block.headers.map((header, cellIndex) => (
                          <TableCell
                            key={`header:${header}:${block.alignments[cellIndex] ?? 'left'}`}
                            align={block.alignments[cellIndex] ?? 'left'}
                            sx={{ px: 1.5, py: 1 }}
                          >
                            <span dangerouslySetInnerHTML={{ __html: header }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {block.rows.map((row) => (
                        <TableRow key={`row:${row.join('|')}`}>
                          {row.map((cell, cellIndex) => (
                            <TableCell
                              key={`cell:${row.join('|')}:${cell}:${block.alignments[cellIndex] ?? 'left'}`}
                              align={block.alignments[cellIndex] ?? 'left'}
                              sx={{ px: 1.5, py: 1 }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: cell }} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            }

            return (
              <Box
                key={blockKey}
                className="activity-log-comment-body__content"
                dangerouslySetInnerHTML={{ __html: block.html }}
              />
            );
          })}
        </Box>
      );
    }

    return (
      <Box sx={sharedContentSx}>
        <EditorContent editor={editor} />
      </Box>
    );
  }

  return fallbackLabel;
};

export default ActivityLogCommentBody;
